import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchRaceEvents, RaceSearchEvent } from '../../../services/eventsService';
import OptionButton from '../shared/OptionButton';
import '../Onboarding.css';

interface StepProps {
  answer: any;
  onAnswer: (value: any) => void;
  answers: Record<string, any>;
}

const RaceSelectionStep: React.FC<StepProps> = ({ answer, onAnswer }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RaceSearchEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const doSearch = useCallback(async (q: string, cursor?: string) => {
    if (q.length < 3) {
      setResults([]);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const res = await searchRaceEvents(q, { searchAfter: cursor });
      if (cursor) {
        setResults((prev) => [...prev, ...res.events]);
      } else {
        setResults(res.events);
      }
      setNextCursor(res.nextCursor);
    } catch (err: any) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doSearch]);

  const selectRace = (event: RaceSearchEvent) => {
    const raceEvent = {
      id: crypto.randomUUID ? crypto.randomUUID() : `race_${Date.now()}`,
      title: event.title,
      location: event.location,
      startDate: event.startDate || undefined,
    };
    onAnswer(raceEvent);
  };

  const selectCustomRace = () => {
    onAnswer({
      id: crypto.randomUUID ? crypto.randomUUID() : `race_${Date.now()}`,
      title: query || 'My race',
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '60vh', overflow: 'hidden' }}>
      <input
        type="text"
        placeholder="Search for a race..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
        style={{
          width: '100%',
          height: 64,
          padding: '20px 16px',
          border: '1px solid rgba(250,253,251,0.16)',
          borderRadius: 16,
          fontSize: 16,
          boxSizing: 'border-box' as const,
          background: 'rgba(250,253,251,0.08)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          color: '#FAFDFB',
          fontFamily: "'Figtree', sans-serif",
          outline: 'none',
        }}
      />

      <div className="onboarding-scrollable" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight * 0.8 && nextCursor && !loading) {
            doSearch(query, nextCursor);
          }
        }}
      >
        {query.length >= 3 && (
          <OptionButton
            label={`"${query}" (custom race)`}
            value="custom"
            selected={answer?.title === query && !answer?.location}
            onClick={selectCustomRace}
          />
        )}

        {results.map((event, i) => (
          <button
            key={`${event.title}-${i}`}
            type="button"
            onClick={() => selectRace(event)}
            style={{
              width: '100%',
              background: answer?.title === event.title ? 'rgba(250,253,251,0.12)' : 'rgba(250,253,251,0.08)',
              border: `2px solid ${answer?.title === event.title ? 'var(--color-primary)' : 'rgba(250,253,251,0.08)'}`,
              borderRadius: 16,
              padding: '16px',
              color: '#FAFDFB',
              fontFamily: "'Figtree', sans-serif",
              fontSize: 16,
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              transition: 'all 0.2s ease',
            }}
          >
            <span style={{ fontWeight: 400 }}>{event.title}</span>
            <div style={{ display: 'flex', gap: 16, fontSize: 14, color: 'rgba(250,253,251,0.5)' }}>
              {event.location && <span>{event.location}</span>}
              {event.startDate && <span>{formatDate(event.startDate)}</span>}
              {event.distances?.length > 0 && <span>{event.distances.join(', ')}</span>}
            </div>
          </button>
        ))}

        {loading && (
          <p style={{ textAlign: 'center', color: 'rgba(250,253,251,0.5)', fontFamily: "'Figtree', sans-serif", fontSize: 14 }}>
            Searching...
          </p>
        )}

        {!loading && query.length >= 3 && results.length === 0 && (
          <p style={{ textAlign: 'center', color: 'rgba(250,253,251,0.5)', fontFamily: "'Figtree', sans-serif", fontSize: 14 }}>
            No races found
          </p>
        )}

        {error && (
          <p style={{ textAlign: 'center', color: 'var(--color-error-light)', fontFamily: "'Figtree', sans-serif", fontSize: 14 }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default RaceSelectionStep;
